import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Grid, IconButton, TextField, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useApi from '../../../shared/hooks/useApi';

const SettingsModal = ({ open, onClose, setLoading, triggerAlert, settingsTrigger }) => {
    const [settings, setSettings] = useState({
        country: 'BE',
        gasBoilerEff: '90',
        gasPriceType: 'fixed',
        gasPrice: '30',
        co2PriceType: 'fixed',
        co2Price: '75',
        eBoilerEff: '95',
        gridCost: '0.12',
        optimizeMonthlyCapacityTariff: false,
    });


    useEffect(() => {
        const stored = localStorage.getItem('leadgen_settings');
        if (stored) {
            try {
                const parsedSettings = JSON.parse(stored);
                setSettings(parsedSettings);
            } catch (error) {
                console.error('Error parsing stored settings:', error);
            }
        }
    }, []);

    const handleChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            localStorage.setItem('leadgen_settings', JSON.stringify(settings));
            settingsTrigger(settings);
            onClose();
            triggerAlert("success", "Settings saved successfully");
        } catch (error) {
            console.error("Error saving settings:", error);
            triggerAlert("error", "Error saving settings");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                maxWidth: 900,
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '10px',
                    backgroundColor: '#f5f5f5',
                },
                '&::-webkit-scrollbar-track': {
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                },
                '&::-webkit-scrollbar-thumb': {
                    borderRadius: '8px',
                    backgroundColor: 'rgba(72,63,94,0.2)',
                    '&:hover': {
                        backgroundColor: 'rgba(72,63,94,0.4)',
                    },
                },
            }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3
                }}>
                    <Typography variant="h5" component="h2">
                        Settings
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Country</InputLabel>
                            <Select
                                value={settings.country}
                                onChange={(e) => handleChange('country', e.target.value)}
                                label="Country"
                            >
                                <MenuItem value="BE">Belgium</MenuItem>
                                <MenuItem value="NL" disabled>Netherlands</MenuItem>
                                <MenuItem value="DE" disabled>Germany</MenuItem>
                                <MenuItem value="FR" disabled>France</MenuItem>
                                <MenuItem value="UK" disabled>United Kingdom</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Gas Boiler Efficiency (%)"
                            type="number"
                            value={settings.gasBoilerEff}
                            onChange={(e) => handleChange('gasBoilerEff', e.target.value)}
                            InputProps={{ step: "0.01" }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Gas Price Type</InputLabel>
                            <Select
                                value={settings.gasPriceType}
                                onChange={(e) => handleChange('gasPriceType', e.target.value)}
                                label="Gas Price Type"
                            >
                                <MenuItem value="fixed">Fixed</MenuItem>
                                <MenuItem value="live" disabled>Live</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Gas Price (€/MWh)"
                            type="number"
                            value={settings.gasPrice}
                            onChange={(e) => handleChange('gasPrice', e.target.value)}
                            InputProps={{ step: "0.01" }}
                        />
                    </Grid>

                    
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>CO2 Price Type</InputLabel>
                            <Select
                                value={settings.co2PriceType}
                                onChange={(e) => handleChange('co2PriceType', e.target.value)}
                                label="CO2 Price Type"
                            >
                                <MenuItem value="fixed">Fixed</MenuItem>
                                <MenuItem value="live" disabled>Live</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="CO2 Price (€/tCO2)"
                            type="number"
                            value={settings.co2Price}
                            onChange={(e) => handleChange('co2Price', e.target.value)}
                            InputProps={{ step: "0.01" }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Electric Boiler Efficiency (%)"
                            type="number"
                            value={settings.eBoilerEff}
                            onChange={(e) => handleChange('eBoilerEff', e.target.value)}
                            InputProps={{ step: "0.01" }}
                        />
                    </Grid>

                

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Grid Cost (€/MWh)"
                            type="number"
                            value={settings.gridCost}
                            onChange={(e) => handleChange('gridCost', e.target.value)}
                            InputProps={{ step: "0.01" }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                disabled
                                    checked={settings.optimizeMonthlyCapacityTariff}
                                    onChange={(e) => handleChange('optimizeMonthlyCapacityTariff', e.target.checked)}
                                />
                            }
                            label="Optimize monthly capacity tariff"
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{ bgcolor: '#1F9562' }}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default SettingsModal;